/**
 * AdminContentEngineReport — /admin/content-engine/report
 *
 * The permanent, comprehensive progress report the operator asked for.
 * Two tabs:
 *   • Report — every article across every portal with URL, content
 *     type, text size (words/chars/sections/faqs/facts/sources),
 *     hero image (with attribution), tags, translation count,
 *     audio-cache footprint, syndication list, correction count.
 *     Rows are hyperlinked to the live article page and each row can
 *     be exported to CSV/JSON so the report survives forever even if
 *     Mongo is wiped.
 *   • Corrections — the open moderator queue populated by the public
 *     Report button. Resolve or Dismiss with a note; both write to
 *     the permanent audit trail.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ExternalLink, RefreshCw, Download, FileText, Flag,
  BadgeCheck, Trash2, Filter, Globe2, Image as ImageIcon, Volume2, Languages,
  ArrowLeft, X, ShieldCheck, Moon, AlertTriangle,
  ClipboardCopy, HeartPulse, Mailbox, Sparkle,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

function Kb(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

function downloadFile(name, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function toCsv(rows) {
  if (!rows.length) return "";
  const header = [
    "portal", "slug", "title", "url", "content_type", "trust_status", "editorial_status",
    "content_version", "master_language", "created_at", "last_updated", "next_review",
    "text_words", "text_chars", "sections", "faqs", "key_facts", "sources",
    "image_url", "image_alt", "image_photographer", "unsplash_id",
    "topics", "geography", "entities",
    "translations_count", "translations_langs",
    "audio_count", "audio_langs", "audio_bytes",
    "syndicated_from", "syndicated_to", "canonical_article_id",
    "corrections_open", "corrections_total",
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = Array.isArray(v) ? v.join(";") : String(v);
    return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
  };
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push([
      r.portal_slug, r.slug, r.title, r.canonical_url, r.content_type, r.trust_status, r.editorial_status,
      r.content_version, r.master_language, r.created_at, r.last_updated, r.next_review_date,
      r.text.text_words, r.text.text_chars, r.text.sections, r.text.faqs, r.text.key_facts, r.text.sources,
      r.image?.url, r.image?.alt_text, r.image?.photographer, r.image?.unsplash_id,
      r.tags.topic, r.tags.geography, r.tags.entities,
      r.translations.count, r.translations.langs,
      r.audio.count, r.audio.langs, r.audio.total_bytes,
      r.syndicated_from, r.syndicated_to, r.canonical_article_id,
      r.corrections.open, r.corrections.total,
    ].map(esc).join(","));
  });
  return lines.join("\n");
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-serif font-bold text-slate-900 text-2xl">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function AdminContentEngineReport() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("report");     // report | corrections
  const [portal, setPortal] = useState("");
  const [portals, setPortals] = useState([]);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [corrStatus, setCorrStatus] = useState("open");
  const [busy, setBusy] = useState(null);
  // New workflow cards
  const [installSnippet, setInstallSnippet] = useState(null);
  const [nativeHealth, setNativeHealth] = useState(null);
  const [trustDigest, setTrustDigest] = useState(null);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const [p, r] = await Promise.all([
        http.get("/content-engine/portals"),
        http.get("/content-engine/admin/report", { params: { portal: portal || undefined, limit: 500 } }),
      ]);
      setPortals(p.data.items || []);
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setReportLoading(false); }
  }, [portal]);

  const loadCorrections = useCallback(async () => {
    try {
      const r = await http.get("/content-engine/admin/corrections", {
        params: { portal: portal || undefined, status: corrStatus, limit: 100 },
      });
      setCorrections(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [portal, corrStatus]);

  useEffect(() => {
    if (user) {
      if (tab === "report") loadReport();
      else loadCorrections();
    }
  }, [user, tab, loadReport, loadCorrections]);

  const resolve = async (id, kind = "resolve") => {
    const note = window.prompt(kind === "resolve" ? "Resolution note (optional):" : "Dismiss note (optional):", "");
    if (note === null) return;
    setBusy(id);
    try {
      await http.post(`/content-engine/admin/corrections/${id}/${kind}`, { resolution_note: note });
      toast.success(kind === "resolve" ? "Resolved" : "Dismissed");
      loadCorrections();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const generateAudio = async (row, lang) => {
    setBusy(`${row.id}-audio-${lang}`);
    try {
      await http.post(`/content-engine/articles/${row.id}/audio`, null, { params: { lang } });
      toast.success(`Audio cached (${lang})`);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const syndicate = async (row) => {
    const targets = window.prompt(`Syndicate "${row.title}" to which portals? (comma-separated slugs, e.g. kerala,indias)`, "");
    if (!targets) return;
    const list = targets.split(",").map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(`${row.id}-syn`);
    try {
      const r = await http.post(`/content-engine/articles/${row.id}/syndicate`, { target_portals: list });
      toast.success(`Created ${r.data.created.length} · Skipped ${r.data.skipped.length}`);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const backfillSources = async (row) => {
    const raw = window.prompt(
      `Paste 2-3 verified URLs (Wikipedia, gov, tourism, WHO, university…). One per line or comma-separated:`,
      "https://en.wikipedia.org/wiki/",
    );
    if (!raw) return;
    const urls = raw.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!urls.length) return;
    const supports = window.prompt("Optional — what do these sources support?", "") || null;
    setBusy(`${row.id}-src`);
    try {
      const r = await http.post(`/content-engine/articles/${row.id}/backfill-sources`, {
        urls,
        supports,
        promote_trust: true,
      });
      toast.success(`Added ${r.data.added} sources · trust = ${r.data.trust_status}`);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const promoteTrust = async (row) => {
    if (row.trust_status === "editor-reviewed") {
      toast.info("Already editor-reviewed");
      return;
    }
    if (row.text?.sources === 0) {
      toast.error("Add at least one verified source first (+ Sources)");
      return;
    }
    if (!window.confirm(`Promote "${row.title}" to editor-reviewed? This signals a human has read the AI draft and confirmed the sources back up every claim.`)) return;
    const note = window.prompt("Editor note (optional — what did you verify?):", "") || null;
    setBusy(`${row.id}-trust`);
    try {
      const r = await http.post(`/content-engine/articles/${row.id}/promote-trust`, { note });
      toast.success(`Trust = ${r.data.trust_status}`);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const seedNightlyNow = async () => {
    const target = portal || (window.confirm("Fire the nightly seed for the WHOLE network right now? (Cancel to enter a single portal slug.)") ? "" : (window.prompt("Portal slug (e.g. socialpulse):", "socialpulse") || "").trim());
    if (target === null) return;
    setBusy("seed-now");
    try {
      const r = await http.post("/content-engine/admin/scheduler/seed-now", null, { params: target ? { portal: target } : {} });
      toast.success(`Nightly seed: ${r.data.processed} new article${r.data.processed === 1 ? "" : "s"}`);
      loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const loadInstallSnippet = async (targetSlug) => {
    const slug = (targetSlug || portal || portals[0]?.slug || "socialpulse").trim();
    setBusy("install");
    try {
      const r = await http.get("/content-engine/portal-install-snippet", { params: { slug, trust_level: "all", limit: 6 } });
      setInstallSnippet(r.data);
      toast.success(`Snippet ready for ${r.data.portal_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const loadNativeHealth = async () => {
    setBusy("health");
    try {
      const r = await http.get("/content-engine/admin/native-health");
      setNativeHealth(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const previewTrustDigest = async () => {
    setBusy("digest-preview");
    try {
      const r = await http.get("/content-engine/admin/trust-digest/preview", { params: portal ? { portal } : {} });
      setTrustDigest(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const sendTrustDigest = async () => {
    const to = window.prompt("Send weekly trust-ladder digest to which email?", user?.email || "");
    if (!to || !to.includes("@")) return;
    setBusy("digest-send");
    try {
      const r = await http.post("/content-engine/admin/trust-digest/send", { to, portal: portal || null });
      if (r.data.already_staged) toast.info(`Already staged for ${to} this week`);
      else toast.success(`Digest staged — ${r.data.total_stuck} stuck draft${r.data.total_stuck === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const copySnippet = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Clipboard blocked"),
    );
  };

  if (authLoading) return (
    <div className="min-h-screen bg-white"><SocialPulseHeader /><div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div><SocialPulseFooter /></div>
  );
  if (!user) return (
    <div className="min-h-screen bg-white"><SocialPulseHeader />
      <div className="min-h-[60vh] grid place-items-center text-center px-6">
        <div>
          <p className="text-slate-700 font-serif text-2xl">Sign in required</p>
          <Link to="/join" className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2 text-sm mt-5">Sign in →</Link>
        </div>
      </div><SocialPulseFooter /></div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-ce-report-page">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Link to="/admin/content-engine" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-violet-700">
            <ArrowLeft className="w-3 h-3" /> Back to CMS
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[.22em] text-violet-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Content Engine
            </div>
            <h1 className="font-serif font-bold text-slate-900 text-2xl sm:text-3xl mt-1">Progress report</h1>
            <p className="text-sm text-slate-500 mt-0.5">A permanent audit of every article ever published across the network.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={portal}
              onChange={(e) => setPortal(e.target.value)}
              data-testid="admin-ce-report-portal-filter"
              className="rounded-full bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none pl-3 pr-6 py-1.5 text-sm font-semibold text-slate-700"
            >
              <option value="">All portals</option>
              {portals.map((p) => <option key={p.slug} value={p.slug}>{p.portal_name}</option>)}
            </select>
            <button
              type="button"
              onClick={seedNightlyNow}
              disabled={busy === "seed-now"}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
              data-testid="admin-ce-seed-now"
              title="Fire the nightly seed pass right now (per-portal or network-wide)"
            >
              <Moon className="w-3 h-3" /> {busy === "seed-now" ? "Seeding…" : "Seed nightly now"}
            </button>
            <button
              type="button"
              onClick={() => (tab === "report" ? loadReport() : loadCorrections())}
              className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-300 hover:ring-slate-400 text-xs font-semibold text-slate-600 px-3 py-1.5"
              data-testid="admin-ce-report-reload"
            >
              <RefreshCw className="w-3 h-3" /> Reload
            </button>
          </div>
        </div>

        {/* Workflow cards — install snippet · native health · trust digest */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
          {/* Install snippet card */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 flex flex-col gap-2" data-testid="admin-ce-install-card">
            <div className="flex items-center gap-2">
              <ClipboardCopy className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-serif font-bold text-slate-900">Sister-site install snippet</h3>
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              One-tap paste snippet for the <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px]">&lt;PulseNetworkFeed/&gt;</code>.
              Choose a portal and generate — the snippet hides itself when the engine is empty.
            </p>
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => loadInstallSnippet()}
                disabled={busy === "install"}
                data-testid="admin-ce-install-generate"
                className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
              >
                {busy === "install" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkle className="w-3 h-3" />}
                Generate for {portal || "…"}
              </button>
            </div>
            {installSnippet && (
              <div className="mt-1 space-y-2" data-testid="admin-ce-install-output">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                  <span>React · {installSnippet.portal_name}</span>
                  <button
                    type="button"
                    onClick={() => copySnippet(installSnippet.react_snippet, "React snippet")}
                    className="rounded-full ring-1 ring-slate-300 hover:ring-slate-400 px-2 py-0.5 text-slate-600"
                    data-testid="admin-ce-install-copy-react"
                  >
                    <ClipboardCopy className="w-3 h-3 inline" /> Copy
                  </button>
                </div>
                <pre className="text-[10px] bg-slate-50 rounded-lg p-2 overflow-x-auto ring-1 ring-slate-200 leading-tight">{installSnippet.react_snippet}</pre>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                  <span>Vanilla-JS embed</span>
                  <button
                    type="button"
                    onClick={() => copySnippet(installSnippet.embed_snippet, "Embed snippet")}
                    className="rounded-full ring-1 ring-slate-300 hover:ring-slate-400 px-2 py-0.5 text-slate-600"
                    data-testid="admin-ce-install-copy-embed"
                  >
                    <ClipboardCopy className="w-3 h-3 inline" /> Copy
                  </button>
                </div>
                <pre className="text-[10px] bg-slate-50 rounded-lg p-2 overflow-x-auto ring-1 ring-slate-200 leading-tight">{installSnippet.embed_snippet}</pre>
                {installSnippet.native_dedupe.enabled && (
                  <div className="text-[10px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg p-2">
                    🛡️ Native dedupe ON — this portal must expose <code>{installSnippet.native_dedupe.index_url}</code> before AI generation is allowed.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Native health card */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 flex flex-col gap-2" data-testid="admin-ce-native-health-card">
            <div className="flex items-center gap-2">
              <HeartPulse className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-serif font-bold text-slate-900">Native content probes</h3>
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              Which sister sites are exposing <code className="rounded bg-slate-100 px-1 py-0.5 text-[10px]">/api/native/index</code>?
              The engine dedupes AI generation against these indices — an unreachable one means AI ships duplicates.
            </p>
            <button
              type="button"
              onClick={loadNativeHealth}
              disabled={busy === "health"}
              data-testid="admin-ce-native-health-check"
              className="w-fit inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
            >
              {busy === "health" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Probe all
            </button>
            {nativeHealth && (
              <div className="mt-1 space-y-1.5" data-testid="admin-ce-native-health-output">
                <div className="text-[11px] text-slate-600">
                  <span className="font-bold text-emerald-700">{nativeHealth.healthy}</span> healthy,
                  {" "}<span className={`font-bold ${nativeHealth.unhealthy > 0 ? "text-rose-600" : "text-slate-500"}`}>{nativeHealth.unhealthy}</span> unreachable
                  <span className="text-slate-400"> · probed {new Date(nativeHealth.checked_at).toLocaleTimeString()}</span>
                </div>
                <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
                  {nativeHealth.portals.map((p) => (
                    <li key={p.slug} className={`p-2 text-[11px] ${p.ok ? "bg-white" : "bg-rose-50/50"}`} data-testid={`admin-ce-native-row-${p.slug}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800">{p.slug}</span>
                        {p.ok ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-2 py-0.5 text-[10px] font-bold">
                            {p.items_count} items · {p.elapsed_ms}ms
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 px-2 py-0.5 text-[10px] font-bold">
                            <AlertTriangle className="w-2.5 h-2.5" /> {p.http || "down"}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate mt-0.5" title={p.url}>{p.url}</div>
                      {!p.ok && p.error && <div className="text-[10px] text-rose-600 mt-0.5">{p.error}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Trust ladder digest card */}
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 flex flex-col gap-2" data-testid="admin-ce-trust-digest-card">
            <div className="flex items-center gap-2">
              <Mailbox className="w-4 h-4 text-sky-600" />
              <h3 className="text-sm font-serif font-bold text-slate-900">Trust-ladder digest</h3>
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              Weekly Monday 09:00 UTC digest of every AI draft stuck without sources.
              Preview here, or send a one-off to your inbox.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={previewTrustDigest}
                disabled={busy === "digest-preview"}
                data-testid="admin-ce-digest-preview"
                className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
              >
                {busy === "digest-preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                Preview
              </button>
              <button
                type="button"
                onClick={sendTrustDigest}
                disabled={busy === "digest-send"}
                data-testid="admin-ce-digest-send"
                className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-sky-300 hover:ring-sky-500 text-sky-700 hover:text-sky-800 text-xs font-semibold px-3 py-1.5"
              >
                {busy === "digest-send" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mailbox className="w-3 h-3" />}
                Send to me
              </button>
            </div>
            {trustDigest && (
              <div className="mt-1 space-y-2" data-testid="admin-ce-digest-output">
                <div className="text-[11px] text-slate-600">
                  Week {trustDigest.week} · <span className="font-bold text-slate-900">{trustDigest.total_stuck}</span> stuck across
                  {" "}<span className="font-bold text-slate-900">{trustDigest.portals_with_stuck}</span> portal(s).
                </div>
                {trustDigest.by_portal.slice(0, 5).map((p) => (
                  <div key={p.slug} className="text-[11px]">
                    <div className="font-bold text-slate-800">{p.portal_name} · {p.count} stuck</div>
                    <ul className="ml-3 mt-0.5 list-disc marker:text-slate-300 text-slate-600 space-y-0.5">
                      {p.items.slice(0, 3).map((it) => (
                        <li key={it.id} className="truncate">{it.title}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                {trustDigest.total_stuck === 0 && (
                  <div className="text-[11px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg p-2">
                    🎉 Clean queue — nothing stuck at ai-draft this week.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-slate-200">
          {[
            ["report", "Report", FileText, report?.totals?.articles],
            ["corrections", "Corrections", Flag, corrections.length],
          ].map(([k, l, Ic, badge]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`admin-ce-report-tab-${k}`}
              className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-semibold ${tab === k ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              <Ic className="w-4 h-4" /> {l} {badge != null && <span className="rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-0.5">{badge}</span>}
            </button>
          ))}
        </div>

        {tab === "report" && (
          <>
            {reportLoading || !report ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading report…</div>
            ) : (
              <>
                {/* Summary + exports */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <StatCard label="Articles" value={report.totals.articles} hint={`across ${report.totals.portals} portals`} />
                  <StatCard label="Total words" value={report.totals.words.toLocaleString()} />
                  <StatCard label="Audio cached" value={Kb(report.totals.audio_bytes)} />
                  <StatCard label="Report generated" value={fmtDate(report.generated_at)} />
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  <button
                    type="button"
                    onClick={() => downloadFile(`content-engine-report-${Date.now()}.csv`, "text/csv", toCsv(report.articles))}
                    data-testid="admin-ce-report-export-csv"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-300 hover:ring-slate-400 text-xs font-semibold text-slate-700 px-3 py-1.5"
                  ><Download className="w-3 h-3" /> Export CSV</button>
                  <button
                    type="button"
                    onClick={() => downloadFile(`content-engine-report-${Date.now()}.json`, "application/json", JSON.stringify(report, null, 2))}
                    data-testid="admin-ce-report-export-json"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-300 hover:ring-slate-400 text-xs font-semibold text-slate-700 px-3 py-1.5"
                  ><Download className="w-3 h-3" /> Export JSON</button>
                </div>

                {/* Per-portal breakdown */}
                {report.per_portal.length > 0 && (
                  <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {report.per_portal.map((pp) => (
                      <div key={pp.portal} className="rounded-2xl bg-white ring-1 ring-slate-200 p-4" data-testid={`admin-ce-report-portal-${pp.portal}`}>
                        <div className="flex items-center gap-2">
                          <Globe2 className="w-4 h-4 text-violet-600" />
                          <h3 className="font-serif font-bold text-slate-900 text-sm">{pp.portal_name}</h3>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                          <div>Articles</div><div className="font-semibold text-slate-800">{pp.articles}</div>
                          <div>Words</div><div className="font-semibold text-slate-800">{pp.words.toLocaleString()}</div>
                          <div>Translations</div><div className="font-semibold text-slate-800">{pp.translations}</div>
                          <div>Audio</div><div className="font-semibold text-slate-800">{Kb(pp.audio_bytes)}</div>
                          <div>Open reports</div><div className="font-semibold text-rose-700">{pp.open_corrections}</div>
                        </div>
                        {Object.keys(pp.content_types).length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {Object.entries(pp.content_types).map(([ct, n]) => (
                              <span key={ct} className="text-[10px] font-semibold rounded-full bg-slate-50 ring-1 ring-slate-200 text-slate-600 px-2 py-0.5">{ct} · {n}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Articles table */}
                <div className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="admin-ce-report-table">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[10px]">
                        <tr>
                          <th className="text-left p-3">Article</th>
                          <th className="text-left p-3">Type</th>
                          <th className="text-left p-3">Size</th>
                          <th className="text-left p-3">Image</th>
                          <th className="text-left p-3">Tags</th>
                          <th className="text-left p-3">Translations</th>
                          <th className="text-left p-3">Audio</th>
                          <th className="text-left p-3">Reports</th>
                          <th className="text-left p-3">Last</th>
                          <th className="text-right p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.articles.map((r) => (
                          <tr key={r.id} className="hover:bg-slate-50/70" data-testid={`admin-ce-report-row-${r.slug}`}>
                            <td className="p-3 min-w-[240px]">
                              <a href={`/pulse-article/${r.slug}?portal=${r.portal_slug}`} target="_blank" rel="noopener" className="font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
                                {r.title || r.slug} <ExternalLink className="w-3 h-3" />
                              </a>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                {r.portal_name} · v{r.content_version} · {r.master_language}
                                {r.syndicated_from && <span className="ml-1 rounded-full bg-amber-50 text-amber-700 text-[9px] font-bold px-1.5 py-0.5">from {r.syndicated_from}</span>}
                                {r.syndicated_to?.length > 0 && <span className="ml-1 rounded-full bg-sky-50 text-sky-700 text-[9px] font-bold px-1.5 py-0.5">→ {r.syndicated_to.join(",")}</span>}
                              </div>
                              <div className="mt-1">
                                {r.trust_status === "editor-reviewed" ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5" data-testid={`admin-ce-report-trust-${r.slug}`}>
                                    <ShieldCheck className="w-2.5 h-2.5" /> Editor reviewed
                                  </span>
                                ) : r.trust_status === "sources-verified" ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5" data-testid={`admin-ce-report-trust-${r.slug}`}>
                                    <BadgeCheck className="w-2.5 h-2.5" /> Sources verified
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5" data-testid={`admin-ce-report-trust-${r.slug}`}>
                                    <AlertTriangle className="w-2.5 h-2.5" /> AI draft
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 font-semibold uppercase tracking-widest text-[9px]">{r.content_type}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <div className="text-slate-800 font-semibold">{r.text.text_words.toLocaleString()} words</div>
                              <div className="text-[10px] text-slate-500">
                                {r.text.text_chars.toLocaleString()} chars · {r.text.sections} § · {r.text.faqs} faqs · {r.text.key_facts} facts · {r.text.sources} src
                              </div>
                            </td>
                            <td className="p-3">
                              {r.image?.url ? (
                                <a href={r.image.url} target="_blank" rel="noopener" className="inline-flex items-center gap-2">
                                  {r.image.thumb && <img src={r.image.thumb} alt={r.image.alt_text || ""} className="w-10 h-10 rounded-md object-cover" />}
                                  <span className="text-[10px] text-slate-500">
                                    {r.image.photographer && <div>by {r.image.photographer}</div>}
                                    {r.image.unsplash_id && <div className="font-mono">{r.image.unsplash_id}</div>}
                                  </span>
                                </a>
                              ) : (
                                <span className="text-slate-400 inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> —</span>
                              )}
                            </td>
                            <td className="p-3 max-w-[180px]">
                              <div className="flex flex-wrap gap-1">
                                {[...(r.tags.topic || []).slice(0, 3), ...(r.tags.geography || []).slice(0, 2)].map((t) => (
                                  <span key={t} className="rounded-full bg-slate-50 ring-1 ring-slate-200 text-slate-600 text-[9px] font-semibold px-1.5 py-0.5">{t}</span>
                                ))}
                              </div>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <div className="text-slate-800 font-semibold inline-flex items-center gap-1"><Languages className="w-3 h-3 text-violet-500" />{r.translations.count}</div>
                              <div className="text-[10px] text-slate-500 truncate max-w-[110px]" title={r.translations.langs.join(", ")}>{r.translations.langs.join(",") || "—"}</div>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <div className="text-slate-800 font-semibold inline-flex items-center gap-1"><Volume2 className="w-3 h-3 text-emerald-500" />{r.audio.count} · {Kb(r.audio.total_bytes)}</div>
                              <div className="text-[10px] text-slate-500 flex flex-wrap gap-0.5 mt-0.5">
                                {["en", "ml", "hi"].map((l) => (
                                  <button
                                    key={l}
                                    type="button"
                                    disabled={busy === `${r.id}-audio-${l}`}
                                    onClick={() => generateAudio(r, l)}
                                    data-testid={`admin-ce-report-audio-${r.slug}-${l}`}
                                    className={`rounded-full ring-1 px-1.5 py-0.5 font-semibold text-[9px] ${r.audio.langs.includes(l) ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "text-slate-500 ring-slate-200 hover:ring-slate-400"}`}
                                    title={r.audio.langs.includes(l) ? "Regenerate audio" : "Generate audio"}
                                  >
                                    {busy === `${r.id}-audio-${l}` ? "…" : l}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className={`rounded-full ring-1 px-1.5 py-0.5 font-bold text-[10px] ${r.corrections.open > 0 ? "bg-rose-50 text-rose-700 ring-rose-200" : "text-slate-500 ring-slate-200"}`}>{r.corrections.open} open</span>
                              <span className="ml-1 text-[10px] text-slate-400">/ {r.corrections.total} total</span>
                            </td>
                            <td className="p-3 whitespace-nowrap text-[10px] text-slate-500">
                              <div>Upd {fmtDate(r.last_updated)}</div>
                              <div>Rev {fmtDate(r.next_review_date)}</div>
                            </td>
                            <td className="p-3 text-right whitespace-nowrap">
                              <div className="inline-flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => backfillSources(r)}
                                  disabled={busy === `${r.id}-src`}
                                  data-testid={`admin-ce-report-sources-${r.slug}`}
                                  className={`rounded-full ring-1 text-[10px] font-bold px-2 py-1 transition-colors ${r.trust_status === "ai-draft" ? "bg-amber-50 text-amber-700 ring-amber-200 hover:ring-amber-400" : "ring-slate-300 text-slate-600 hover:ring-emerald-400 hover:text-emerald-700"}`}
                                  title={r.trust_status === "ai-draft" ? "Add verified sources → promote to sources-verified" : "Add more sources"}
                                >
                                  + Sources
                                </button>
                                <button
                                  type="button"
                                  onClick={() => promoteTrust(r)}
                                  disabled={busy === `${r.id}-trust` || r.trust_status === "editor-reviewed"}
                                  data-testid={`admin-ce-report-promote-${r.slug}`}
                                  className={`rounded-full ring-1 text-[10px] font-bold px-2 py-1 transition-colors ${r.trust_status === "editor-reviewed" ? "bg-emerald-50 text-emerald-700 ring-emerald-200 cursor-default" : r.trust_status === "sources-verified" ? "bg-emerald-600 hover:bg-emerald-700 text-white ring-emerald-500/30" : "ring-slate-300 text-slate-400 hover:text-slate-600 hover:ring-slate-400"}`}
                                  title={r.trust_status === "editor-reviewed" ? "Already at the top of the trust ladder" : r.trust_status === "sources-verified" ? "Promote to editor-reviewed (highest trust rung)" : "Add sources first to unlock editor-reviewed"}
                                >
                                  <span className="inline-flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> {r.trust_status === "editor-reviewed" ? "Reviewed" : "Editor OK"}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => syndicate(r)}
                                  disabled={busy === `${r.id}-syn`}
                                  data-testid={`admin-ce-report-syndicate-${r.slug}`}
                                  className="rounded-full ring-1 ring-slate-300 hover:ring-sky-400 hover:text-sky-700 text-slate-600 text-[10px] font-bold px-2 py-1"
                                  title="Syndicate to more portals"
                                >
                                  Syndicate
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {report.articles.length === 0 && (
                          <tr><td colSpan={10} className="p-8 text-center text-slate-500 text-sm italic">No articles yet. Create one from the CMS.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent audit trail */}
                {report.recent_audit?.length > 0 && (
                  <div className="mt-6 rounded-2xl bg-white ring-1 ring-slate-200 p-5">
                    <h3 className="font-serif font-bold text-slate-900 text-sm mb-3">Recent activity</h3>
                    <ul className="space-y-1.5 text-xs">
                      {report.recent_audit.slice(0, 20).map((a) => (
                        <li key={a.id} className="flex gap-3">
                          <span className="text-slate-400 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                          <span className="rounded-full bg-slate-100 text-slate-700 px-1.5 font-semibold text-[10px]">{a.action}</span>
                          <span className="text-slate-500 truncate">by {a.actor} · {JSON.stringify(a.details).slice(0, 80)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {tab === "corrections" && (
          <>
            <div className="flex items-center gap-2 mb-4">
              {["open", "resolved", "dismissed"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setCorrStatus(s)}
                  data-testid={`admin-ce-corr-status-${s}`}
                  className={`rounded-full ring-1 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${corrStatus === s ? "bg-violet-50 ring-violet-300 text-violet-700" : "ring-slate-200 text-slate-500 hover:ring-slate-400"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
              {corrections.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm italic">No {corrStatus} reports.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {corrections.map((c) => (
                    <li key={c.id} className={`p-4 flex flex-wrap gap-3 ${c.priority >= 20 ? "bg-rose-50/40 border-l-4 border-rose-400" : ""}`} data-testid={`admin-ce-corr-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {c.priority >= 30 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white ring-1 ring-rose-700 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5" title="Auto-routed to top of queue" data-testid={`admin-ce-corr-priority-${c.id}`}>
                              <AlertTriangle className="w-3 h-3" /> Top priority
                            </span>
                          )}
                          {c.priority === 20 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-800 ring-1 ring-orange-300 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5" title="High-priority reader-breaking report" data-testid={`admin-ce-corr-priority-${c.id}`}>
                              <AlertTriangle className="w-3 h-3" /> High
                            </span>
                          )}
                          <span className="rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">{c.kind}</span>
                          {c.lang && <span className="text-[10px] font-semibold text-slate-500">{c.lang}</span>}
                          <a href={`/pulse-article/${c.article_slug}?portal=${c.portal_slug}`} target="_blank" rel="noopener" className="text-sm font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
                            {c.article_title || c.article_slug} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <p className="mt-1 text-sm text-slate-700 leading-relaxed">{c.message}</p>
                        <p className="mt-1 text-[11px] text-slate-500">
                          {new Date(c.created_at).toLocaleString()}
                          {c.reporter_email && <span> · from {c.reporter_email}</span>}
                          {c.resolution_note && <span> · resolved with: "{c.resolution_note}"</span>}
                        </p>
                      </div>
                      {c.status === "open" && (
                        <div className="flex flex-col gap-1.5">
                          <button
                            type="button"
                            onClick={() => resolve(c.id, "resolve")}
                            disabled={busy === c.id}
                            data-testid={`admin-ce-corr-resolve-${c.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1"
                          >
                            <BadgeCheck className="w-3 h-3" /> Resolve
                          </button>
                          <button
                            type="button"
                            onClick={() => resolve(c.id, "dismiss")}
                            disabled={busy === c.id}
                            data-testid={`admin-ce-corr-dismiss-${c.id}`}
                            className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-slate-400 text-slate-600 text-xs font-semibold px-3 py-1"
                          >
                            <X className="w-3 h-3" /> Dismiss
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
      <SocialPulseFooter />
    </div>
  );
}
